import {useCallback, useState} from "react";
import {useLocale, useMessages} from "i18n";
import {arrivalReturnPath, focusLandmarkPath} from "components/sheetOpener";
import {CouponClaimDialogView} from "offers/ladder/CouponClaimDialog";
import {limitWallOfferEndsOn, type LimitWallPresentation} from "./limitWallOffer";
import type {LimitWallOfferState} from "./useLimitWallOffer";
import {useLimitWallClaim} from "./useLimitWallClaim";

/**
 * The dialog the limit-wall offer card's button opens over the composer (kitecorp/transferify-api-service#2206,
 * on the send screen since kitecorp/transferify-ui#3197).
 *
 * <b>On the send panel, not the app shell.</b> The promo link can point at any public route, so its
 * dialog lives on the shell; this link cannot: the api's redirect always lands on `/?wall=`, and the
 * card that opens this dialog is the panel's own slide-out card. Opening it in place is what keeps the
 * selection the sender has rebuilt through the payment.
 *
 * <b>The shared coupon view, two ways.</b> A live coupon is the ticket and its two prices; once the day's
 * redemptions are spent (C7) the same view shows the plan at its regular price with the note, and Subscribe
 * is an ordinary checkout for that plan. The ticket's expiry line names the weekday the link ends, from
 * the api's own `expiresAt`, and nothing else on the screen mentions the 72 hours (owner 2026-10-03). It
 * owns its focus restore, as the promo dialog does, because the card that opened it may have tucked or
 * left the stack by the time it closes, so there is no opener for MUI's trap to return to.
 */
export function LimitWallDialog({wall}: {wall: LimitWallOfferState}) {
	const claimable = claimableOf(wall.presentation);
	// The last claimable presentation, held through the dialog's EXIT: a refusal whose re-read says the link
	// expired takes the presentation to `expired` while the dialog is open, and unmounting it there would
	// skip `onTransitionExited` and its focus restore, leaving a keyboard reader on `body`. So the held one
	// renders closed until it has exited. The hook memoises the presentation, so this settles in one pass.
	const [held, setHeld] = useState<ClaimablePresentation | null>(claimable);
	if (claimable !== null && claimable !== held) setHeld(claimable);
	const release = useCallback(() => setHeld(null), []);
	const shown = claimable ?? held;
	if (shown === null) return null;
	return <LimitWallClaimDialog wall={wall} presentation={shown} open={wall.open && claimable !== null}
		onExited={claimable === null ? release : undefined}/>;
}

type ClaimablePresentation = Extract<LimitWallPresentation, {kind: "coupon" | "regular"}>;

function claimableOf(presentation: LimitWallPresentation): ClaimablePresentation | null {
	return presentation.kind === "coupon" || presentation.kind === "regular" ? presentation : null;
}

/** Mounted only while there is something to claim or to close, so the claim hook always has a presentation. */
function LimitWallClaimDialog({wall, presentation, open, onExited}: {
	wall: LimitWallOfferState; presentation: ClaimablePresentation; open: boolean; onExited?: () => void;
}) {
	const {offerLadder} = useMessages();
	const {locale} = useLocale();
	const {token, activeVisit, dismiss, refusedAtTill, paid} = wall;
	const claim = useLimitWallClaim(presentation, token, activeVisit, dismiss, refusedAtTill, paid);
	const exited = useCallback(() => {
		focusLandmarkPath(arrivalReturnPath(document));
		onExited?.();
	}, [onExited]);
	// Read at render, so a dialog left open past midnight names the day as of now rather than as of its opening.
	const endsOn = limitWallOfferEndsOn(presentation.arrival.expiresAt, locale, Date.now()) ?? undefined;
	if (presentation.kind === "regular") {
		return <CouponClaimDialogView gift={presentation.quote} note={offerLadder.limitWall.offersUsedUp} claim={claim}
			onClose={dismiss} open={open} onTransitionExited={exited}/>;
	}
	return <CouponClaimDialogView gift={presentation.gift} discountsLeft={presentation.discountsLeft} endsOn={endsOn}
		claim={claim} onClose={dismiss} open={open} onTransitionExited={exited}/>;
}
